import React, { useState } from 'react';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import useMediaQuery from '../hooks/useMediaQuery';

// Cottagecore design tokens
const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
};

const Contact = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <div style={{ backgroundColor: T.paper, minHeight: '100vh', fontFamily: T.sans }}>
      <SEO
        title="Contact"
        description="Get in touch with the Cahoots team. Questions, feedback, or ideas — we want to hear from you."
      />

      {/* Hero */}
      <div style={{
        textAlign: 'center',
        padding: isMobile ? '48px 24px' : '56px 24px',
      }}>
        <div style={{
          fontFamily: T.hand,
          fontSize: '1.1rem',
          color: T.orange,
          marginBottom: '12px',
          letterSpacing: '0.02em',
        }}>
          say hello ✦
        </div>
        <h1 style={{
          fontFamily: T.sans,
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: 800,
          color: T.ink,
          margin: '0 0 16px',
          letterSpacing: '-0.5px',
          lineHeight: 1.1,
        }}>
          Get in touch
        </h1>
        <p style={{
          fontFamily: T.sans,
          fontSize: '1.05rem',
          color: T.inkMuted,
          maxWidth: 440,
          margin: '0 auto',
          lineHeight: 1.65,
        }}>
          Questions, feedback, or ideas — we read every message.
        </p>
      </div>

      {/* Main content */}
      <div style={{ padding: isMobile ? '0 20px 80px' : '0 24px 96px' }}>
        <div style={{
          maxWidth: 860,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1.6fr',
          gap: isMobile ? '32px' : '48px',
          alignItems: 'start',
        }}>

          {/* Left: contact info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ContactMethod
              emoji="✉"
              label="General"
              value="admin@cahoots.cc"
              href="mailto:admin@cahoots.cc"
            />
            <ContactMethod
              emoji="◎"
              label="Enterprise Sales"
              value="sales@cahoots.cc"
              href="mailto:sales@cahoots.cc"
            />
            <div style={{
              marginTop: '8px',
              padding: '16px',
              background: T.paperDeep,
              borderRadius: T.r3,
              border: `1px dashed ${T.rule}`,
            }}>
              <p style={{ fontFamily: T.hand, fontSize: '1.05rem', color: T.ink2, margin: '0 0 6px', lineHeight: 1.5 }}>
                Usually reply within a day or two
              </p>
              <p style={{ fontFamily: T.sans, fontSize: '0.8rem', color: T.inkMuted, margin: 0, lineHeight: 1.5 }}>
                We're a small team — your message goes straight to a real person.
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div style={{
            background: T.card,
            border: `1px solid ${T.rule}`,
            borderRadius: T.r4,
            boxShadow: T.sh2,
            overflow: 'hidden',
          }}>
            {/* Form header */}
            <div style={{
              background: T.paperDeep,
              borderBottom: `1px dashed ${T.rule}`,
              padding: '18px 28px',
            }}>
              <p style={{
                fontFamily: T.sans,
                fontWeight: 700,
                fontSize: '0.95rem',
                color: T.ink,
                margin: 0,
              }}>Send a message</p>
            </div>

            <ContactForm />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// ============================================================================
// CONTACT FORM
// ============================================================================

const ContactForm = () => {
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div style={{ padding: '48px 28px', textAlign: 'center' }}>
        <div style={{ fontFamily: T.hand, fontSize: '2rem', color: T.orange, marginBottom: '12px' }}>
          ✦
        </div>
        <p style={{ fontFamily: T.sans, fontWeight: 700, fontSize: '1rem', color: T.ink, marginBottom: '8px' }}>
          Message sent!
        </p>
        <p style={{ fontFamily: T.sans, fontSize: '0.875rem', color: T.inkMuted, margin: 0, lineHeight: 1.6 }}>
          We'll get back to you within a day or two.
        </p>
      </div>
    );
  }

  return (
    <form
      action="https://formsubmit.co/admin@cahoots.cc"
      method="POST"
      onSubmit={() => setSubmitted(true)}
      style={{ padding: '24px 28px 28px' }}
    >
      <input type="hidden" name="_subject" value="New Contact Form Submission - Cahoots" />
      <input type="hidden" name="_captcha" value="true" />
      <input type="hidden" name="_template" value="table" />
      <input type="hidden" name="_next" value={window.location.href} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <Field label="Name">
          <FormInput type="text" name="name" required placeholder="Your name" />
        </Field>
        <Field label="Email">
          <FormInput type="email" name="email" required placeholder="you@example.com" />
        </Field>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <Field label="Message">
          <FormTextarea name="message" required rows={5} placeholder="What's on your mind?" />
        </Field>
      </div>

      <button
        type="submit"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '13px 24px',
          backgroundColor: T.orange,
          color: '#fff',
          fontSize: '0.95rem',
          fontWeight: 700,
          fontFamily: T.sans,
          border: 'none',
          borderRadius: T.r3,
          cursor: 'pointer',
          letterSpacing: '0.01em',
          transition: 'background 0.15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = T.orangeDark; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = T.orange; }}
      >
        Send message
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      </button>
    </form>
  );
};

// ============================================================================
// COMPONENTS
// ============================================================================

const ContactMethod = ({ emoji, label, value, href }) => (
  <div style={{
    display: 'flex',
    gap: '14px',
    alignItems: 'center',
    padding: '14px 16px',
    background: T.card,
    border: `1px solid ${T.rule}`,
    borderRadius: T.r3,
    boxShadow: T.sh1,
  }}>
    <div style={{
      width: 38,
      height: 38,
      backgroundColor: T.paperDeep,
      borderRadius: T.r3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.1rem',
      flexShrink: 0,
      border: `1px solid ${T.rule}`,
    }}>
      {emoji}
    </div>
    <div>
      <p style={{ fontFamily: T.sans, fontSize: '0.75rem', color: T.inkFaint, margin: '0 0 2px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </p>
      <a href={href} style={{ fontFamily: T.sans, fontSize: '0.9rem', fontWeight: 600, color: T.ink2, textDecoration: 'none' }}
        onMouseEnter={e => { e.currentTarget.style.color = T.orange; }}
        onMouseLeave={e => { e.currentTarget.style.color = T.ink2; }}
      >
        {value}
      </a>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block',
      fontFamily: T.sans,
      fontSize: '0.8rem',
      fontWeight: 700,
      color: T.ink2,
      marginBottom: '6px',
      letterSpacing: '0.02em',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const inputBase = {
  width: '100%',
  padding: '10px 13px',
  fontSize: '0.875rem',
  fontFamily: '"Nunito", ui-sans-serif, sans-serif',
  lineHeight: '1.5',
  color: '#2E2109',
  backgroundColor: '#FDF7E5',
  border: '1px solid #D7C9A1',
  borderRadius: '8px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
};

const FormInput = (props) => (
  <input
    style={inputBase}
    onFocus={e => {
      e.target.style.borderColor = '#EA7C1F';
      e.target.style.boxShadow = '0 0 0 3px rgba(234,124,31,0.15)';
    }}
    onBlur={e => {
      e.target.style.borderColor = '#D7C9A1';
      e.target.style.boxShadow = 'none';
    }}
    {...props}
  />
);

const FormTextarea = (props) => (
  <textarea
    style={{ ...inputBase, resize: 'none' }}
    onFocus={e => {
      e.target.style.borderColor = '#EA7C1F';
      e.target.style.boxShadow = '0 0 0 3px rgba(234,124,31,0.15)';
    }}
    onBlur={e => {
      e.target.style.borderColor = '#D7C9A1';
      e.target.style.boxShadow = 'none';
    }}
    {...props}
  />
);

export default Contact;
